import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import HelpIcon from '../../../../../components/help/HelpIcon';

const HELP_TOPIC = { module: 'modelo-semantico', topic: 'espacio-de-analisis' };

const DEFAULT_FORM_STATE = {
  name: '',
  description: '',
  business_area: '',
  is_active: true,
  change_reason: ''
};

const WorkspaceFormDialog = ({ open, mode, workspace, onClose, onSubmit, isSubmitting }) => {
  const isEditMode = mode === 'edit';
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isEditMode && workspace) {
      setFormData({
        name: workspace.name || '',
        description: workspace.description || '',
        business_area: workspace.business_area || '',
        is_active: workspace.is_active !== false,
        change_reason: ''
      });
    } else {
      setFormData(DEFAULT_FORM_STATE);
    }
    setErrors({});
  }, [open, workspace, isEditMode]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    let payload;
    if (isEditMode && workspace) {
      payload = {
        ...workspace,
        ...formData,
        semantic_workspace_id: workspace.semantic_workspace_id
      };
    } else {
      const { change_reason, ...rest } = formData;
      payload = rest;
    }

    onSubmit?.(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: {
          backgroundColor: 'var(--wb-bg-surface)',
          border: '1px solid var(--wb-border-16)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>
        {isEditMode ? 'Modificar espacio de análisis' : 'Crear espacio de análisis'}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'var(--wb-border)' }}>
        <TextField
          margin="normal"
          label="Nombre"
          name="name"
          fullWidth
          value={formData.name}
          onChange={handleChange}
          error={Boolean(errors.name)}
          helperText={errors.name}
          InputLabelProps={{ shrink: true }}
          InputProps={{ endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="nombre" /></InputAdornment> }}
          sx={{
            '& .MuiInputBase-root': {
              color: 'var(--wb-text-title)',
              backgroundColor: 'var(--wb-bg-overlay-90)'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--wb-border-35)'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--wb-blue)'
            },
            '& .MuiInputLabel-root': {
              color: 'var(--wb-muted-80)'
            },
            '& .MuiFormHelperText-root': {
              color: 'var(--wb-danger)'
            }
          }}
        />
        <TextField
          margin="normal"
          label="Descripción"
          name="description"
          fullWidth
          minRows={3}
          maxRows={6}
          multiline
          value={formData.description}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          InputProps={{ endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="descripción" /></InputAdornment> }}
          sx={{
            '& .MuiInputBase-root': {
              color: 'var(--wb-text-title)',
              backgroundColor: 'var(--wb-bg-overlay-90)'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--wb-border-35)'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--wb-blue)'
            },
            '& .MuiInputLabel-root': {
              color: 'var(--wb-muted-80)'
            }
          }}
        />
        <TextField
          margin="normal"
          label="Área de negocio (opcional)"
          name="business_area"
          fullWidth
          value={formData.business_area}
          onChange={handleChange}
          helperText="Ej. 'Retail y consumo masivo', 'Finanzas corporativas' -- ayuda a la IA a generar descripciones más precisas para las tablas de este espacio."
          InputLabelProps={{ shrink: true }}
          InputProps={{ endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="área-de-negocio-opcional" /></InputAdornment> }}
          sx={{
            '& .MuiInputBase-root': {
              color: 'var(--wb-text-title)',
              backgroundColor: 'var(--wb-bg-overlay-90)'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--wb-border-35)'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--wb-blue)'
            },
            '& .MuiInputLabel-root': {
              color: 'var(--wb-muted-80)'
            },
            '& .MuiFormHelperText-root': {
              color: 'var(--wb-muted-60)'
            }
          }}
        />
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'var(--wb-primary)'
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'var(--wb-primary)'
                  },
                  '& .MuiSwitch-switchBase': {
                    color: 'var(--wb-border-30)'
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: 'var(--wb-x-bg-94a3b840, rgba(148, 163, 184, 0.25))'
                  }
                }}
              />
            }
            label="Espacio activo"
            sx={{ color: 'var(--wb-text)' }}
          />
          <HelpIcon {...HELP_TOPIC} anchor="espacio-activo" />
        </Stack>
        {isEditMode && (
          <TextField
            margin="normal"
            label="Motivo del cambio (opcional)"
            name="change_reason"
            fullWidth
            value={formData.change_reason}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="motivo-del-cambio-opcional-solo-al-editar" /></InputAdornment> }}
            sx={{
              '& .MuiInputBase-root': {
                color: 'var(--wb-text-title)',
                backgroundColor: 'var(--wb-bg-overlay-90)'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--wb-border-35)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--wb-blue)'
              },
              '& .MuiInputLabel-root': {
                color: 'var(--wb-muted-80)'
              }
            }}
          />
        )}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'var(--wb-muted-70)' }}>
            {isEditMode
              ? 'Al guardar se actualizará la versión del espacio y se registrará la auditoría en el backend.'
              : 'Al crear un nuevo espacio de análisis se añadirá automáticamente al usuario actual como propietario.'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ color: 'var(--wb-muted)' }}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
        >
          {isSubmitting ? 'Guardando…' : isEditMode ? 'Guardar cambios' : 'Crear espacio'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WorkspaceFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
  workspace: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool
};

WorkspaceFormDialog.defaultProps = {
  mode: 'edit',
  workspace: null,
  isSubmitting: false
};

export default WorkspaceFormDialog;
